import { useRef, useState, useCallback, useMemo } from "react";
import { Box, IconButton, Button, Typography, Collapse } from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { toast } from "react-toastify";
import api from "../../../api";

const FORMATS = ["bold", "italic", "image"];

const CHAR_GROUPS = [
  {
    group: "Powers",
    chars: [
      { label: "²", title: "squared" },
      { label: "³", title: "cubed" },
      { label: "⁴", title: "to the 4th" },
      { label: "⁵", title: "to the 5th" },
      { label: "⁶", title: "to the 6th" },
      { label: "⁷", title: "to the 7th" },
      { label: "⁸", title: "to the 8th" },
      { label: "⁹", title: "to the 9th" },
      { label: "¹⁰", title: "to the 10th" },
      { label: "ⁿ", title: "to the nth" },
      { label: "⁰", title: "superscript 0" },
      { label: "¹", title: "superscript 1" },
    ],
  },
  {
    group: "Fractions",
    chars: [
      { label: "½", title: "one half" },
      { label: "¼", title: "one quarter" },
      { label: "¾", title: "three quarters" },
      { label: "⅓", title: "one third" },
      { label: "⅔", title: "two thirds" },
      { label: "⅕", title: "one fifth" },
      { label: "⅖", title: "two fifths" },
      { label: "⅗", title: "three fifths" },
      { label: "⅘", title: "four fifths" },
      { label: "⅛", title: "one eighth" },
      { label: "⅜", title: "three eighths" },
      { label: "⅝", title: "five eighths" },
      { label: "⅞", title: "seven eighths" },
    ],
  },
  {
    group: "Operators",
    chars: [
      { label: "×", title: "multiply" },
      { label: "÷", title: "divide" },
      { label: "±", title: "plus or minus" },
      { label: "√", title: "square root" },
      { label: "∛", title: "cube root" },
      { label: "≠", title: "not equal to" },
      { label: "≤", title: "less than or equal to" },
      { label: "≥", title: "greater than or equal to" },
      { label: "∞", title: "infinity" },
    ],
  },
  {
    group: "Geometry & Measure",
    chars: [
      { label: "°", title: "degrees" },
      { label: "∠", title: "angle" },
      { label: "△", title: "triangle" },
      { label: "□", title: "square / rectangle" },
      { label: "⊥", title: "perpendicular" },
      { label: "∥", title: "parallel" },
      { label: "π", title: "pi" },
      { label: "cm²", title: "square centimetres" },
      { label: "m²", title: "square metres" },
      { label: "km²", title: "square kilometres" },
      { label: "cm³", title: "cubic centimetres" },
      { label: "m³", title: "cubic metres" },
    ],
  },
  {
    group: "Currency & Other",
    chars: [
      { label: "£", title: "pound sterling" },
      { label: "p", title: "pence" },
      { label: "%", title: "percent" },
      { label: "∑", title: "sum" },
      { label: "→", title: "arrow / maps to" },
    ],
  },
];

/**
 * Lightweight rich-text editor for answer option fields (MCQ, Matching, Ordering).
 *
 * Lazy-mounts Quill only when clicked so that N options on screen don't all
 * maintain live Quill instances. At rest it renders an HTML preview.
 *
 * Props:
 *   value       — HTML string (current option text)
 *   onChange    — (html: string) => void
 *   placeholder — optional placeholder string
 */
const AnswerOptionRichEditor = ({ value, onChange, placeholder }) => {
  const [editing, setEditing] = useState(false);
  const [charPanelOpen, setCharPanelOpen] = useState(true);
  const containerRef = useRef(null);
  const quillRef = useRef(null);
  const uploadingRef = useRef(false);

  const imageHandler = useCallback(function () {
    const quill = this.quill;
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/png,image/jpeg,image/webp";
    input.click();
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const formData = new FormData();
        formData.append("image", file);
        const res = await api.post("/admin/question-bank/upload-image", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const url = res.data?.data?.image_url;
        if (url) {
          const range = quill.getSelection(true) || { index: quill.getLength() };
          quill.insertEmbed(range.index, "image", url, "user");
          quill.setSelection(range.index + 1);
        }
      } catch {
        toast.error("Image upload failed");
      }
    };
  }, []);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [["bold", "italic"], ["image"], ["clean"]],
        handlers: { image: imageHandler },
      },
    }),
    [imageHandler]
  );

  const handleChange = async (html) => {
    const base64Re = /src="(data:image\/[^"]+)"/g;
    if (!base64Re.test(html) || uploadingRef.current) {
      onChange(html);
      return;
    }
    uploadingRef.current = true;
    let processed = html;
    for (const match of [...html.matchAll(/src="(data:image\/[^"]+)"/g)]) {
      try {
        const fetchRes = await fetch(match[1]);
        const blob = await fetchRes.blob();
        const ext = blob.type.split("/")[1] || "png";
        const file = new File([blob], `pasted.${ext}`, { type: blob.type });
        const formData = new FormData();
        formData.append("image", file);
        const res = await api.post("/admin/question-bank/upload-image", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const url = res.data?.data?.image_url;
        if (url) processed = processed.replace(match[0], `src="${url}"`);
      } catch {
        toast.error("Pasted image upload failed");
      }
    }
    uploadingRef.current = false;
    onChange(processed);
  };

  const insertChar = (char) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    const range = quill.getSelection() ?? { index: Math.max(0, quill.getLength() - 1) };
    quill.insertText(range.index, char, "user");
    quill.setSelection(range.index + char.length);
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (containerRef.current && !containerRef.current.contains(document.activeElement)) {
        setEditing(false);
      }
    }, 150);
  };

  const isEmpty = !value || value === "<p><br></p>";

  return (
    <Box ref={containerRef} onBlur={handleBlur}>
      {editing ? (
        <>
          <Box sx={{ mb: 0.75, bgcolor: "#f5f3ff", borderRadius: 1, border: "1px solid #ede9fe", overflow: "hidden" }}>
            <Box
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setCharPanelOpen((v) => !v)}
              sx={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                px: 0.75, py: 0.5, cursor: "pointer",
                "&:hover": { bgcolor: "rgba(124,58,237,0.06)" },
              }}
            >
              <Typography variant="caption" sx={{ color: "#7c3aed", fontWeight: 600 }}>
                Insert character
              </Typography>
              <IconButton size="small" onMouseDown={(e) => e.preventDefault()} sx={{ p: 0.25, color: "#7c3aed" }}>
                {charPanelOpen ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
              </IconButton>
            </Box>
            <Collapse in={charPanelOpen}>
              <Box sx={{ px: 0.75, pb: 0.75 }}>
                {CHAR_GROUPS.map(({ group, chars }) => (
                  <Box key={group} sx={{ mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: "#9ca3af", fontSize: "0.65rem", display: "block", mb: 0.25 }}>
                      {group}
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.4 }}>
                      {chars.map(({ label, title }) => (
                        <Button
                          key={label}
                          size="small"
                          title={title}
                          onMouseDown={(e) => { e.preventDefault(); insertChar(label); }}
                          sx={{
                            minWidth: 36, height: 26, px: 0.5,
                            fontSize: "0.82rem", fontFamily: "monospace",
                            border: "1px solid #ddd6fe", bgcolor: "#fff",
                            color: "#3730a3", lineHeight: 1, textTransform: "none",
                            "&:hover": { bgcolor: "#ede9fe" },
                          }}
                        >
                          {label}
                        </Button>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Box>

          <Box
            sx={{
              "& .ql-toolbar": {
                borderRadius: "4px 4px 0 0", padding: "3px 6px", flexWrap: "wrap",
                "& button": { padding: "2px 4px", width: "auto" },
              },
              "& .ql-container": { minHeight: 48, fontSize: "0.9rem", borderRadius: "0 0 4px 4px" },
              "& .ql-editor": { minHeight: 48, padding: "6px 10px" },
              "& .ql-editor img": { maxWidth: "100%", maxHeight: 180, borderRadius: 2, display: "block", margin: "4px 0" },
              "& .ql-editor.ql-blank::before": { fontSize: "0.875rem" },
            }}
          >
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={value || ""}
              onChange={handleChange}
              modules={modules}
              formats={FORMATS}
              placeholder={placeholder}
            />
          </Box>
        </>
      ) : (
        <Box
          onClick={() => setEditing(true)}
          sx={{
            minHeight: 38,
            border: "1px solid rgba(0,0,0,0.23)",
            borderRadius: 1,
            px: 1.5,
            py: 0.75,
            cursor: "text",
            fontSize: "0.9rem",
            lineHeight: 1.5,
            bgcolor: "#fff",
            color: isEmpty ? "text.disabled" : "text.primary",
            "&:hover": { borderColor: "rgba(0,0,0,0.87)" },
            "& img": { maxWidth: "100%", maxHeight: 120, borderRadius: 1, verticalAlign: "middle" },
            "& p": { margin: 0 },
            "& strong": { fontWeight: 700 },
            "& em": { fontStyle: "italic" },
          }}
        >
          {isEmpty ? (
            <span style={{ opacity: 0.42 }}>{placeholder || "Click to edit"}</span>
          ) : (
            <span dangerouslySetInnerHTML={{ __html: value }} />
          )}
        </Box>
      )}
    </Box>
  );
};

export default AnswerOptionRichEditor;
